// (C) Copyright 2025 Hewlett Packard Enterprise Development LP
/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react'
import { Box, DataTable, Text, Spinner, Button } from 'grommet'
import { CircleInformation, Refresh } from 'grommet-icons'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'

import { NoDataInfo } from '../../../components'

import StatusIcon from './StatusIcon'
import RecentDeliveriesModal from './RecentDeliveriesModal'

const RecentDeliveryInfo = ({ deliveryInfo, retryHandler }) => {
  const { t } = useTranslation(['automations'])
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [selectedDelivery, setSelectedDelivery] = useState(null)

  useEffect(() => {
    if (deliveryInfo) {
      setIsLoading(false)
    }
  }, [deliveryInfo])
  const getLatestEntries = (data) => {
    const latestEntries = {}
    data.forEach((entry) => {
      const { type } = entry.requestBody
      const { createdAt } = entry
      if (!type.includes('com.')) {
        entry.retryStatus = ''
      }
      if (
        !latestEntries[type] ||
        new Date(latestEntries[type].createdAt) < new Date(createdAt)
      ) {
        latestEntries[type] = entry
      }
    })
    return latestEntries
  }
  const latestEntries = getLatestEntries(deliveryInfo)

  const columns = [
    {
      property: 'eventType',
      header: t('automations:webhooks.event_type'),
      primary: true,
      render: (datum) => (
        <Box direction="row" align="center" gap="xsmall">
          <Text>{datum.requestBody.type}</Text>
        </Box>
      )
    },
    {
      property: 'eventId',
      header: t('automations:webhooks.event_id'),
      primary: true,
      render: (datum) => (
        <Box direction="row" align="center" gap="xsmall">
          <Text>{datum.requestBody.id}</Text>
        </Box>
      )
    },
    {
      property: 'httpResponseCode',
      header: t('automations:webhooks.response'),
      primary: true,
      render: (datum) => (
        <Box direction="row" align="center" gap="xsmall">
          <StatusIcon
            status={datum.httpResponseCode === 200 ? 'ACTIVE' : 'ERROR'}
          />
          <Text data-testid="httpsResponseCode">{datum.httpResponseCode}</Text>
        </Box>
      )
    },
    {
      property: 'createdAt',
      header: t('automations:webhooks.date'),
      render: (datum) =>
        datum.createdAt ? (
          <Text data-testid="createdAt">
            {dayjs(datum.createdAt).format('MM/DD/YYYY, hh:mm:ss A')}
          </Text>
        ) : (
          'N/A'
        )
    },
    {
      property: 'createdAt',
      header: '',
      width: 100,
      render: (datum) => {
        if (!datum.retryStatus) {
          return null
        }
        return datum.retryStatus === 'RETRY' &&
          latestEntries[datum.requestBody.type]?.createdAt ===
            datum.createdAt ? (
          <Button
            style={{
              display: 'inline-block',
              backgroundColor: 'transparent',
              padding: '6px 18px 6px 2px'
            }}
            type="button"
            onClick={() => retryHandler(datum.failureId)}
            label={
              <Box flex align="center" direction="row" gap="xsmall">
                <Refresh />
                {t('automations:webhooks.retry_label')}
              </Box>
            }
          />
        ) : (
          datum.httpResponseCode !== 200 && (
            <Box flex align="center" direction="row" gap="xsmall">
              <div>{t('automations:webhooks.retried_label')}</div>
            </Box>
          )
        )
      }
    }
  ]

  const onRowClick = (event) => {
    setSelectedDelivery(event.datum)
    setIsOpen(true)
  }

  const closePopup = () => {
    setIsOpen(false)
  }

  const renderContent = () => {
    return deliveryInfo && deliveryInfo?.length > 0 ? (
      <Box width="xlarge" height="auto">
        <DataTable
          columns={columns}
          data={deliveryInfo}
          onClickRow={onRowClick}
          data-testid="events-recent-del-table"
        />
      </Box>
    ) : (
      <Box width="xlarge" height="small" justify="center">
        <NoDataInfo
          icon={<CircleInformation color="black" size="large" />}
          title={t('automations:webhooks.no_recent_deliveries')}
          gap="xsmall"
          gapTitleSubtitle="xsmall"
          testId="no-delivery-info"
        />
      </Box>
    )
  }

  return (
    <Box>
      {isLoading ? <Spinner /> : renderContent()}
      {isOpen && selectedDelivery && (
        <RecentDeliveriesModal
          onSetOpen={setIsOpen}
          onClosePopup={closePopup}
          deliveryInfo={[selectedDelivery]}
        />
      )}
    </Box>
  )
}

export default RecentDeliveryInfo
